import React, { useEffect, useState } from 'react'
import { ReactComponent as StarFilled } from '../../../assets/svgs/star-filled.svg'
import { ReactComponent as StarOutline } from '../../../assets/svgs/star-outlined.svg'
import CryptoJS from 'crypto-js'

import './card.scss'
import { InfoTooltip } from '../../atoms/info-tooltip/info-tooltip'
import { t } from '../../../utils/i18n'
import { useFavorites } from '../../../context/FavoritesContext'
import { Link } from 'react-router-dom'
import { useStorage } from '../../../customHooks/useStorage'
import { CanBeAnything } from '../../../types'
import { useAuth } from '../../../customHooks/useAuth'
import axios from '../../../utils/axios'

export interface FavCardProps {
  title: string
  description: string
  icon?: string
  isFavorite?: boolean
  allowedApps?: string[]
  appSysId: string
  link: string
}

const Card: React.FC<FavCardProps> = ({
  title,
  description,
  icon,
  isFavorite = false,
  allowedApps,
  appSysId,
  link,
}) => {
  const { toggleFavorite } = useFavorites()
  let isAppNotAuthorized = false
  if (allowedApps?.length) {
    isAppNotAuthorized = allowedApps?.includes(appSysId)
  }
  const secretKey = process.env.REACT_APP_UPA_REDIRECTION_KEY ?? 'REACT_APP_UPA_REDIRECTION_KEY'

  const { value }: CanBeAnything = useStorage('user', '', 'session')
  const [updatedURL, setUpdatedURL] = useState('')
  const { user } = useAuth()

  const dataWithTs = {
    ...value,
    currentTimeStamp: Date.now(),
    appId: appSysId,
  }

  const sessionString = JSON.stringify(dataWithTs)
  const cipherText = CryptoJS.AES.encrypt(sessionString, secretKey).toString()
  const encodedURL = encodeURIComponent(cipherText)

  useEffect(() => {
    if (isAppNotAuthorized) {
      setUpdatedURL(`${link}/upasession`)
    }
  }, [appSysId, allowedApps])

  const passSessionDataToServer = async () => {
    try {
      const config = {
        method: 'POST',
        url: `${link}/api/auth/upa/session`,
        data: { sessionData: encodedURL },
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const result = await axios(config)

      return result
    } catch {
      console.log('Unable to create session Id')
    }
  }

  const handleRedirection = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    link: string
  ) => {
    e.preventDefault()
    const res = await passSessionDataToServer()
    if (res?.data?.data?.sessionId) {
      if (window.location.hostname === 'localhost') {
        // window.location.href = `http://localhost:3001/upasession?sId=${res?.data?.data?.sessionId}`
      } else {
        window.location.href = `${link}/upasession?sId=${res?.data?.data?.sessionId}`
      }
    }
  }

  const component = (
    <Link
      to={updatedURL}
      className={`card ${isFavorite ? 'favorite' : ''} ${allowedApps && !isAppNotAuthorized ? 'disabled' : ''}`}
      aria-label={title}
      aria-describedby="heading"
      onClick={(e) => handleRedirection(e, link)}
    >
      <div className="card-header">
        <div className="left">
          {icon && <img src={icon} alt={title} className="icon-img" loading="eager" />}
          <span className="form-label">{title}</span>
        </div>

        {isFavorite ? (
          <InfoTooltip tooltipContent={t('remove.favorites')}>
            <StarFilled
              className="star favorite"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                toggleFavorite(appSysId)
              }}
            />
          </InfoTooltip>
        ) : (
          <InfoTooltip tooltipContent={t('add.favorites')}>
            <StarOutline
              className="star unfavorite"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                toggleFavorite(appSysId)
              }}
            />
          </InfoTooltip>
        )}
      </div>
      <hr className="divider" />
      <p id="heading" className="heading-description">
        {description}
      </p>
    </Link>
  )

  return allowedApps && !isAppNotAuthorized ? (
    <InfoTooltip tooltipContent={t('app.not.authorized')} followCursor>
      <div style={{ cursor: 'not-allowed', width: '100%', marginBottom: '12px' }}>
        <div style={{ pointerEvents: 'none' }}>{component}</div>
      </div>
    </InfoTooltip>
  ) : (
    component
  )
}

export default Card
